import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { getAllProductsWithRoles, editPowUser, getPowUserDetails } from "../../../services/api/userAdministration.api";
import { MessageCard } from "../../../components/MessageCard";
import { TextField, FormControl, FormLabel, Typography, Chip } from "@material-ui/core";
import {
	PowUserResponse,
	EditPowUserRoleRequest,
	EditPowUserRequest,
	EditPowUserResponse,
} from "../../../models/Administration";
import { showSnack } from "../../../utils/component";
import { UserAuditHistory } from "./UserAuditHistoryList";
import { UserProductRoleEditor } from "./UserProductRoleEditor";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useSnackbar } from "notistack";
import { UserEditFailedActionsList } from "./UserEditFailedActionsList";

interface UserEditProps {
	//The user principal name for the user to edit.
	userPrincipalName: string;
	// Function to enable or disable page blocking on navigation event
	togglePageNavigationBlocking: (isBlocking: boolean) => void;
}

const useStyles = makeStyles((theme) => ({
	titleWrapper: {
		width: "95%",
		display: "flex",
		justifyContent: "space-between",
	},
	title: {
		marginBottom: theme.spacing(3),
	},
	userAuditHistoryTitle: {
		marginBottom: theme.spacing(1),
		marginTop: theme.spacing(2),
		marginLeft: theme.spacing(1.5),
	},
	userAuditHistoryList: {
		width: "96%",
	},
	formControl: {
		marginLeft: theme.spacing(1.3),
		marginRight: theme.spacing(1),
		marginTop: "9px",
	},
	formControlLabel: {
		fontSize: theme.typography.caption.fontSize,
		margin: 0,
	},
	inputField: {
		width: 320,
		marginBottom: 0,
		marginTop: "11px",
	},
	progress: {
		margin: theme.spacing(2),
	},
}));

const renderUserProductEdit = (
	user: PowUserResponse,
	products: object,
	submitChanges: (requests: EditPowUserRoleRequest[]) => void,
	isBlockingNavigation: (isBlockingNavigation: boolean) => boolean,
) => {
	return (
		<div data-testid="user-edit-product-component">
			{user ? (
				<div>
					<UserProductRoleEditor
						userDetails={user}
						products={products}
						submitChanges={submitChanges}
						isBlockingNavigation={isBlockingNavigation}
					></UserProductRoleEditor>
				</div>
			) : (
				<MessageCard message="User not found." textAlign="left" />
			)}
		</div>
	);
};

/**
 * This component is used for fetching user data and feeding the fetched data
 * to various components for display on the user edit page
 *  @param props
 */
export const UserEdit = (props: UserEditProps) => {
	const classes = useStyles();
	const { enqueueSnackbar } = useSnackbar();
	const [user, setUser] = React.useState<PowUserResponse>();
	const [products, setProducts] = React.useState<object>();
	const [userName, setUserName] = React.useState(props.userPrincipalName);
	const [fetchData, setFetchData] = React.useState(true);
	const [editedUserName, setEditedUserName] = React.useState(props.userPrincipalName);
	const [failedAddActions, setFailedAddActions] = React.useState<string[]>();
	const [failedRemoveActions, setFailedRemoveActions] = React.useState<string[]>();
	const [failedChangeUserName, setFailedChangeUserName] = React.useState<string>();

	const submitRoleChanges = async (requests: EditPowUserRoleRequest[]) => {
		try {
			if (requests.length > 0 || editedUserName !== userName) {
				const request: EditPowUserRequest = {
					userPrincipalName: editedUserName,
					editRequests: requests,
				};
				const userEditResponse = await editPowUser(request, userName)();
				const responseData: EditPowUserResponse = userEditResponse.data;

				if (responseData.failedUserNameChange == false) {
					window.history.pushState({}, "", window.location.origin + "/Administration/Users/" + editedUserName);
					setUserName(editedUserName);
					setFailedChangeUserName(undefined);
				} else {
					setFailedChangeUserName(editedUserName);
				}

				if (
					responseData.failedAddActions.length == 0 &&
					responseData.failedRemoveActions.length == 0 &&
					!responseData.failedUserNameChange
				) {
					showSnack(enqueueSnackbar, "User Changes Successful", "success");
				} else {
					showSnack(enqueueSnackbar, "Some User Changes Failed. See below for details.", "error");
				}

				setFailedAddActions(responseData.failedAddActions);
				setFailedRemoveActions(responseData.failedRemoveActions);
				setUser(undefined);
				setFetchData(true);
			}
		} catch {
			showSnack(enqueueSnackbar, "Failed To Edit User.", "error");
		}
	};

	function isBlockingNavigation(isEditedProductRoles: boolean) {
		const isBlocking = isEditedProductRoles || editedUserName != userName;
		props.togglePageNavigationBlocking(isBlocking);
		return isBlocking;
	}

	function getUserDetailsAndProducts() {
		const getUserDetailsAndProductRoles = async () => {
			if (!user) {
				const userDetailsPromise = getPowUserDetails(userName)();
				setUser((await userDetailsPromise).data);
			}
			if (!products) {
				const productAndRolesPromise = getAllProductsWithRoles()();
				setProducts((await productAndRolesPromise).data);
			}
		};
		if (fetchData) {
			getUserDetailsAndProductRoles();
			setFetchData(false);
		}

		return <CircularProgress data-testid="user-edit-loading-indicator" className={classes.progress} />;
	}

	return (
		<div>
			<div className={classes.titleWrapper}>
				<Typography className={classes.title} variant="h4" data-testid="user-edit-title">
					{userName}
				</Typography>
				{user && user.pdpGlobalAdministrator && <Chip label="PDP Global Administrator" variant="outlined" />}
			</div>
			<div>
				<UserEditFailedActionsList
					userName={userName}
					editedUserName={failedChangeUserName}
					failedAddActions={failedAddActions}
					failedRemoveActions={failedRemoveActions}
				></UserEditFailedActionsList>
				<div data-testid="user-name-edit-field">
					<FormControl className={classes.formControl}>
						<FormLabel className={classes.formControlLabel}>User Principal Name</FormLabel>
						<TextField
							className={classes.inputField}
							value={editedUserName}
							margin="normal"
							onChange={(e) => setEditedUserName(e.target.value)}
						/>
					</FormControl>
				</div>
			</div>
			{(user && products && (
				<div>
					<div data-testid={"user-audit-history-component"}>
						<div style={{ marginTop: 20 }}>
							{renderUserProductEdit(user, products, submitRoleChanges, isBlockingNavigation)}
						</div>
						<div className={classes.userAuditHistoryList}>
							<h4 className={classes.userAuditHistoryTitle}>User Audit History</h4>
							<UserAuditHistory userPrincipalName={userName} />
						</div>
					</div>
				</div>
			)) ||
				getUserDetailsAndProducts()}
		</div>
	);
};
